import { FC, useEffect, useRef } from 'react';
import NewOrderService from '@/features/orders/components/NewOrder/NewOrderServices/NewOrderService';
import { useTranslation } from 'react-i18next';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import type { NewOrderFormData } from '@/features/orders/validators/NewOrderValidator';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import EServiceLookup from '@/features/orders/components/lookups/EServiceLookup';
import { useOrderServicesLookupApi } from '@/features/orders/hooks/useOrderServicesLookupApi';

type Props = {
	control: Control<NewOrderFormData>;
	setValue: UseFormSetValue<NewOrderFormData>;
};

const NewOrderServicesList: FC<Props> = ({ control, setValue }) => {
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const previousServiceIdRef = useRef<string | undefined>(undefined);

	const selectedServices = useWatch({ control, name: 'serviceId' }) ?? [];
	const selectedServiceId = selectedServices[0]?.id;

	const { orderServicesLookupList } = useOrderServicesLookupApi();

	useEffect(() => {
		if (!selectedServiceId && orderServicesLookupList?.length) {
			setValue('serviceId', [orderServicesLookupList[0]]);
		}
	}, [orderServicesLookupList, selectedServiceId, setValue]);

	useEffect(() => {
		if (previousServiceIdRef.current === selectedServiceId) return;
		if (previousServiceIdRef.current !== undefined) {
			setValue('lines', []);
		}
		previousServiceIdRef.current = selectedServiceId;
	}, [selectedServiceId, setValue]);

	return (
		<div
			className={cn('shrink-0 flex flex-col gap-4', isMobile && 'gap-4')}>
			<div className={cn('flex flex-col gap-2.5', isMobile && 'gap-3')}>
				<h3 className={cn(isMobile && 'text-base')}>
					{t(
						'main.orders.components.create.components.services.title',
					)}
				</h3>
				<EServiceLookup
					control={control}
					fieldName='serviceId'
					label=''
					placeholder={t(
						'main.orders.components.create.components.services.title',
					)}
					showSearch
					addResetButton={false}
				/>
			</div>
			<div
				className={cn(
					'p-6 flex flex-col gap-6 border border-popover-100 rounded-2.5xl bg-popover-10',
					isMobile && 'p-0 border-0 bg-popover-30',
				)}>
				{selectedServiceId ? (
					<NewOrderService
						control={control}
						isMobile={isMobile}
					/>
				) : (
					<span className='text-sm text-popover-800'>
						{t(
							'main.orders.components.create.components.services.noServices',
						)}
					</span>
				)}
			</div>
		</div>
	);
};

export default NewOrderServicesList;
